import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const BASE_URL = 'https://trinitydentalarts.com';

// Human-readable labels for each path segment
const SEGMENT_LABELS = {
  'dental-implants': 'Dental Implants',
  'cosmetic-dentistry': 'Cosmetic Dentistry',
  'general-dentistry': 'General Dentistry',
  'invisalign': 'Invisalign',
  'full-mouth-restoration': 'Full Mouth Restoration',
  'emergency-dentistry': 'Emergency Dentistry',
  'sedation-dentistry': 'Sedation Dentistry',
  'laser-dentistry': 'Laser Dentistry',
  'regenerative-dentistry': 'Regenerative Dentistry',
  'oral-surgery': 'Oral Surgery',
  'periodontal-disease-treatment': 'Periodontal Disease Treatment',
  'preventative-dentistry': 'Preventative Dentistry',
  'single-implant': 'Single Tooth Implant',
  'fixed-new-teeth': 'Fixed New Teeth',
  'all-on-x': 'All-on-4 & All-on-6',
  'same-day-crowns': 'Same-Day Crowns',
  'dental-bridges': 'Dental Bridges',
  'dentures': 'Dentures',
  'bone-grafting': 'Bone Grafting',
  'sinus-grafts': 'Sinus Grafts',
  'about': 'About',
  'services': 'Services',
  'meet-the-doctor': 'Meet the Doctor',
  'our-technology': 'Our Technology',
  'our-results': 'Our Results',
  'financing-options': 'Financing Options',
  'dental-insurance': 'Dental Insurance',
  'office-policies': 'Office Policies',
  'patient-education': 'Patient Education',
  'resources': 'Resources',
  'faq': 'FAQ',
  'dental-implants-faq': 'Dental Implants FAQ',
  'dental-assessment': 'Dental Assessment',
  'smile-visualizer': 'Smile Visualizer',
  'reviews': 'Patient Reviews',
  'contact-us': 'Contact Us',
  'book-appointment': 'Book Appointment',
  'special-offer': 'Special Offer',
  'blog': 'Blog',
  'dental-implants-guide': 'Dental Implants Guide',
  'all-on-4-guide': 'All-on-4 Guide',
  'cosmetic-dentistry-guide': 'Cosmetic Dentistry Guide',
  'implant-research-data': 'Implant Research Data',
  'trinity-fl': 'Trinity, FL',
  'tampa-fl': 'Tampa, FL',
  'clearwater-fl': 'Clearwater, FL',
  'new-port-richey-fl': 'New Port Richey, FL',
  'st-petersburg-fl': 'St. Petersburg, FL',
  'palm-harbor-fl': 'Palm Harbor, FL',
  'privacy-policy': 'Privacy Policy',
  'terms-of-service': 'Terms of Service',
  'accessibility-statement': 'Accessibility Statement',
};

function toLabel(segment) {
  return SEGMENT_LABELS[segment] || segment
    .split('-')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export default function BreadcrumbSchema() {
  const { pathname } = useLocation();

  useEffect(() => {
    // Remove previously injected breadcrumb script
    document.querySelectorAll('script[data-breadcrumb-schema]').forEach(s => s.remove());

    // Skip home and utility pages
    if (pathname === '/' || pathname.startsWith('/admin') || pathname.includes('.txt') || pathname.includes('security')) return;

    // A page that ships its own BreadcrumbList (PageSEO jsonLd or a StructuredData
    // block) is authoritative; a second, path-derived trail would contradict it.
    const pageHasBreadcrumb = [...document.querySelectorAll('script[type="application/ld+json"]:not([data-breadcrumb-schema])')]
      .some((s) => /"@type"\s*:\s*"BreadcrumbList"/.test(s.textContent || ''));
    if (pageHasBreadcrumb) return;

    const segments = pathname.replace(/^\//, '').split('/').filter(Boolean);

    const items = [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": BASE_URL
      }
    ];

    let cumPath = '';
    segments.forEach((seg, i) => {
      cumPath += '/' + seg;
      items.push({
        "@type": "ListItem",
        "position": i + 2,
        "name": toLabel(seg),
        "item": BASE_URL + cumPath
      });
    });

    const schema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": items
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-breadcrumb-schema', 'true');
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      document.querySelectorAll('script[data-breadcrumb-schema]').forEach(s => s.remove());
    };
  }, [pathname]);

  return null;
}